// client/src/components/seo/SeoHeader.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LangSelect from "../front/LangSelect.jsx";
import { SEO_LANGS, SEO_FALLBACK_LANG, getSeoLangLinks } from "../../pages/seo/seoLangConfig.js";
import SeoBreadcrumbs from "./SeoBreadcrumbs.jsx";

import tgIcon from "../../img/icons/tg1.png";
import waIcon from "../../img/icons/wa1.png";
import instIcon from "../../img/icons/inst1.png";

const HEADER_COPY = {
  en: {
    langLabel: "Language",
    openMenu: "Open menu",
    quickCheck: "Quick check",
    demo: "WATCH VIDEO",
    register: "TRY FOR FREE",
    homeLabel: "Go to homepage",
  },
  de: {
    langLabel: "Sprache",
    openMenu: "Menü öffnen",
    quickCheck: "Kurzer Check",
    demo: "VIDEO ANSEHEN",
    register: "KOSTENLOS TESTEN",
    homeLabel: "Zur Startseite",
  },
  no: {
    langLabel: "Språk",
    openMenu: "Åpne meny",
    quickCheck: "Rask sjekk",
    demo: "SE VIDEO",
    register: "PRØV GRATIS",
    homeLabel: "Gå til forsiden",
  },
};

function normalizeSeoHeaderLang(value) {
  const v = String(value || "").trim().toLowerCase();
  return SEO_LANGS.includes(v) ? v : SEO_FALLBACK_LANG;
}

export default function SeoHeader({ lang = SEO_FALLBACK_LANG, langLinks = {} }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const currentLang = normalizeSeoHeaderLang(lang);

  const copy = HEADER_COPY[currentLang] || HEADER_COPY.en;

  const safeLangLinks = useMemo(() => {
    return getSeoLangLinks(langLinks);
  }, [langLinks]);

  const visibleHeaderLangs = useMemo(() => {
  const langsFromLinks = SEO_LANGS.filter((code) => safeLangLinks[code]);
  return langsFromLinks.length ? langsFromLinks : SEO_LANGS;
}, [safeLangLinks]);

  const homePath = `/${currentLang}/`;

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  const onChangeLang = useCallback(
    (nextValue) => {
      const nextLangRaw =
        typeof nextValue === "string" ? nextValue : nextValue?.target?.value;

      const nextLang = normalizeSeoHeaderLang(nextLangRaw);

      if (nextLang === currentLang) {
        closeMenu();
        return;
      }

      const nextPath = safeLangLinks[nextLang];

      if (nextPath) {
        navigate(nextPath);
        closeMenu();
        return;
      }

      const fallbackPath =
        safeLangLinks[SEO_FALLBACK_LANG] || `/${SEO_FALLBACK_LANG}/`;

      navigate(fallbackPath);
      closeMenu();
    },
    [navigate, safeLangLinks, currentLang, closeMenu]
  );

  return (
  <>
    <section className="header--seo">
      <div className="header--seo__wrapper">
        <div className="header--seo__logo-lang">
          <Link
            to={homePath}
            className="logo-wrapper-land"
            aria-label={copy.homeLabel}
            onClick={closeMenu}
          />

          <div className="header--seo__lang">
            <LangSelect
              value={currentLang}
              onChange={onChangeLang}
              availableLangs={visibleHeaderLangs}
              label={copy.langLabel}
            />
          </div>
        </div>

        <div className="header--seo__nav">
          <button
            type="button"
            className={`header--seo__menuButton${
              menuOpen ? " header--seo__menuButton--open" : ""
            }`}
            aria-label={copy.openMenu}
            aria-pressed={menuOpen}
            aria-expanded={menuOpen}
            aria-controls="seoNavList"
            onClick={toggleMenu}
          >
            <span />
            <span />
            <span />
          </button>

          <ul
            id="seoNavList"
            className={`header--seo__navList${
              menuOpen ? " header--seo__navList--open" : ""
            }`}
          >

            <li className="header--seo__navItem header--seo__navItem--socials">
              <a
                className="header--seo__socialLink"
                href="https://wa.me/380967750115"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                onClick={closeMenu}
              >
                <img
                  className="header--seo__socialIcon"
                  src={waIcon}
                  alt=""
                  aria-hidden="true"
                />
              </a>

              <a
                className="header--seo__socialLink"
                href="https://t.me/Ruzhytskyy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
                onClick={closeMenu}
              >
                <img
                  className="header--seo__socialIcon"
                  src={tgIcon}
                  alt=""
                  aria-hidden="true"
                />
              </a>

              <a
                className="header--seo__socialLink"
                href="https://www.instagram.com/_digi.space?igsh=NHhvOHIycHZweWUw&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                onClick={closeMenu}
              >
                <img
                  className="header--seo__socialIcon"
                  src={instIcon}
                  alt=""
                  aria-hidden="true"
                />
              </a>
            </li>

            <li className="header--seo__navItem">
              <Link
                to={`/${currentLang}/demo`}
                className="qrArticle__btn"
                onClick={closeMenu}
              >
                {copy.demo}
              </Link>
            </li>

            

            <li className="header--seo__navItem">
              <Link
                to={`/${currentLang}/register`}
                className="header--seo__button-try-now"
                onClick={closeMenu}
              >
                {copy.register}
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <SeoBreadcrumbs lang={currentLang} />
  </>
  );
}